<template>
  <v-container fluid>
    <v-alert :value="!hasPermission(permission)" :type="`info`" class="mt-4 mb-4"
             v-html="`<span>` + trans('app.no_access_tooltip') + `</span>`">
    </v-alert>
    <v-layout row wrap>
      <v-flex xs8>
        <h4>{{ title | trans }}</h4>
      </v-flex>
      <v-flex xs4>
        <v-btn class="pull-right" color="primary" @click="updateNutrients()" :disabled="!hasPermission(permission)" :loading="nutrientsIsLoading">
          {{ 'app.save' | trans }}
        </v-btn>
      </v-flex>
      <v-flex sm6>
        <v-layout column class="mr-4 nutrients-container">
          <h4>{{ `app.settings.available_nutrients` | trans }}</h4>
          <v-text-field
              :disabled="!hasPermission(permission)"
              v-model="availableNutrientsSearch"
              @input="filterAvailableNutrients()"
              :label="`app.search.search` | trans"
              solo
              class="mt-3"
          ></v-text-field>
          <draggable class="list-group available-nutrients" tag="ul" v-model="availableNutrients"
                     v-bind="dragOptions"
                     :disabled="true">
            <transition-group type="transition">
              <li class="list-group-item" v-for="nutrient in availableNutrients"
                  :key="nutrient.order">
                <v-tooltip
                    top
                    :disabled="!hasPermission(permission)"
                >
                  <template v-slot:activator="{ on }">
                    <i :class="`fa fa-plus-circle` + (hasPermission(permission) ? ` text-green` : ` text-gray`)"
                       @click="addNutrient(nutrient)"
                       v-on="on"></i>
                  </template>
                  <span>{{ `app.settings.add_nutrient` | trans }}</span>
                </v-tooltip>
                <span :class="{'text-gray': !hasPermission(permission)}">{{ nutrient.name }}</span>
              </li>
            </transition-group>
          </draggable>
        </v-layout>
      </v-flex>
      <v-flex sm6>
        <v-layout column class="nutrients-container">
          <h4>{{ `app.settings.selected_nutrients` | trans }}</h4>
          <div class="progress-bar-container mt-3">
            <v-progress-linear
                ref="progressBar"
                :color="hasPermission(permission) ? (this.showMaxNutrientsWarning ? `error` : `success`) : `#d2d6de`"
                height="15"
                :value="progress"
            ></v-progress-linear>
            <v-messages v-if="showMaxNutrientsWarning" :value="this.getMaxNutrientsError()"
                        :color="`error`"></v-messages>
          </div>
          <draggable v-model="nutrients"
                     v-bind="dragOptions"
                     :disabled="!hasPermission(permission)"
                     class="list-group nutrients"
                     tag="ul">
            <li class="list-group-item"
                v-for="nutrient in nutrients"
                :key="nutrient.order">
              <v-tooltip
                  top
                  :disabled="!hasPermission(permission)"
              >
                <template v-slot:activator="{ on }">
                  <i :class="`fa fa-minus-circle` + (hasPermission(permission) ? ` text-red` : ` text-gray`)"
                     @click="removeNutrient(nutrient)" v-on="on"></i>
                </template>
                <span>{{ `app.settings.remove_nutrient` | trans }}</span>
              </v-tooltip>
              <span :class="{'text-gray': !hasPermission(permission)}">{{ nutrient.name }}</span>
            </li>
          </draggable>
        </v-layout>
      </v-flex>
    </v-layout>
  </v-container>
</template>
<script>
import {mapActions, mapGetters, mapState} from "vuex";
import {trans} from "../../../app";
import draggable from "vuedraggable";
import {round} from "../../../utils/money";

export default {
  components: {
    draggable
  },
  props: {
    title: {
      type: String,
      required: true
    },
    permission: {
      type: String,
      required: true
    },
    route: {
      type: String,
      required: true
    },
    maxNutrients: {
      type: Number,
      required: true
    },
  },
  data: () => ({
    nutrientsIsLoading: false,
    availableNutrientsSearch: '',
    availableNutrients: [],
    showMaxNutrientsWarning: false,
    nutrients: [],
    excludedNutrients: [
      'Fenyloalanina + Tyrozyna',
      'Metionina + Cystyna',
    ],
  }),
  computed: {
    ...mapState(['dictionaries']),
    ...mapGetters([
      'hasPermission',
    ]),
    dragOptions() {
      return {
        animation: 0,
        group: "description",
        disabled: false,
      };
    },
    progress() {
      return round(this.nutrients.length * 100 / this.maxNutrients, 0);
    }
  },
  methods: {
    ...mapActions(['FETCH_DICTIONARIES']),
    async updateNutrients() {
      this.showMaxNutrientsWarning = false;

      this.nutrientsIsLoading = true;

      try {
        await axios.put(this.route, this.nutrients);
        this.$toast.success(trans('app.actions.saved', {name: trans('app.pages.settings.index').toLowerCase()}));
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.$toast.error(trans('app.save_failed'));
        } else {
          this.sentryCaptureException(error);
        }
      }

      this.nutrientsIsLoading = false;
    },
    filterAvailableNutrients() {
      if (this.availableNutrientsSearch !== '') {
        this.availableNutrients = this.allAvailableNutrients.filter((nutrient) => nutrient.name.toLowerCase().includes(this.availableNutrientsSearch.toLowerCase()));
      } else {
        this.availableNutrients = this.allAvailableNutrients;
      }
    },
    addNutrient(nutrient) {
      if (this.hasPermission(this.permission) === false) {
        return;
      }
      if (this.nutrients.length === this.maxNutrients) {
        this.showMaxNutrientsWarning = true;
        return;
      }
      this.availableNutrients = this.availableNutrients.filter((selectedNutrient) => {
        return selectedNutrient.name !== nutrient.name;
      });
      this.availableNutrients = this.sortNutrients(this.availableNutrients);

      this.nutrients.push(nutrient);
    },
    removeNutrient(nutrient) {
      if (this.hasPermission(this.permission) === false) {
        return;
      }

      this.nutrients = this.nutrients.filter((selectedNutrient) => {
        return selectedNutrient.name !== nutrient.name;
      });
      if (this.nutrients.length <= 10) {
        this.showMaxNutrientsWarning = false;
      }

      this.availableNutrients.push(nutrient);
      this.availableNutrients = this.sortNutrients(this.availableNutrients);
    },
    sortNutrients(nutrients) {
      return nutrients.sort((nutrientA, nutrientB) => {
        if (nutrientA.name < nutrientB.name) {
          return -1;
        }
        if (nutrientA.name > nutrientB.name) {
          return 1;
        }
        return 0;
      });
    },
    getMaxNutrientsError() {
      return [trans('app.settings.max_nutrients_print', {max: this.maxNutrients})];
    },
    async setCurrentNutrients(nutrients) {
      this.nutrients = nutrients
      await this.FETCH_DICTIONARIES();
      this.allAvailableNutrients = this.dictionaries.nutrients.map((nutrient, index) => {
        return {
          name: nutrient.name,
          order: index + 1,
          fixed: false,
          display_name: nutrient.print_name,
        }
      }).filter((nutrient) => {
        return this.nutrients.map((nutrientPrint) => nutrientPrint.name).includes(nutrient.name) === false &&
            this.excludedNutrients.includes(nutrient.name) === false;
      });
      this.allAvailableNutrients = this.sortNutrients(this.allAvailableNutrients);
      this.availableNutrients = this.allAvailableNutrients;
    }
  },
  watch: {
    isDragging(newValue) {
      if (newValue) {
        this.delayedDragging = true;
        return;
      }
      this.$nextTick(() => {
        this.delayedDragging = false;
      });
    }
  }
}
</script>
<style scoped>
.list-group {
  background-color: rgba(255, 255, 255, 0.8);
  height: 100%;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.list-group-item > i:hover {
  cursor: pointer;
}

.nutrients > .list-group-item:hover {
  cursor: grab;
}

.list-group-item.sortable-ghost {
  cursor: grabbing;
}

.nutrients-container {
  height: 100%;
}

.progress-bar-container {
  min-height: 76px;
}

.available-nutrients .list-group-item {
  float: left;
  width: 50%;
}
</style>
